import { Info } from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "./button";
import { cn } from "./cn";
import { Dialog } from "./dialog";
import { FormAlert } from "./form-alert";

/**
 * "Are you sure?" for a one-click change: what will happen, then confirm. Mount it only while open.
 * `tone: "danger"` for changes that hurt someone (suspending), with a red confirm button.
 */
export function ConfirmDialog({
  title,
  subject,
  effect,
  confirmLabel,
  tone = "primary",
  busy,
  error,
  onConfirm,
  onClose,
  children,
}: {
  title: string;
  /** Who or what it's about, under the title. */
  subject: string;
  effect: ReactNode;
  confirmLabel: string;
  tone?: "primary" | "danger";
  busy: boolean;
  error: unknown;
  onConfirm: () => void;
  onClose: () => void;
  children?: ReactNode;
}) {
  const danger = tone === "danger";
  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={title}
      description={subject}
      busy={busy}
      onSubmit={(event) => {
        event.preventDefault();
        onConfirm();
      }}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant={danger ? "danger" : "primary"} loading={busy}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <FormAlert error={error} />
      <div className="flex flex-col gap-4">
        <p
          className={cn(
            "flex gap-2.5 rounded-xl px-3.5 py-3 text-sm text-ink",
            danger ? "bg-danger-soft" : "bg-info-soft",
          )}
        >
          <Info
            className={cn("mt-0.5 size-[18px] shrink-0", danger ? "text-danger" : "text-info")}
            aria-hidden="true"
          />
          <span>{effect}</span>
        </p>
        {children}
      </div>
    </Dialog>
  );
}
